import React, { useMemo, useState, type ReactElement } from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { FileTextOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';

import { useTranslation } from '@govard/i18n-core/react';
import { useDataViewPagination } from '../../../shared/dataview/useDataViewPagination.js';
import { useCreateTemplateMutation, useTemplates, useUpdateTemplateMutation } from '../hooks/useTemplates.js';

const { Text } = Typography;
const { TextArea } = Input;

type TemplateChannel = 'all' | 'email' | 'sms' | 'in_app';

interface TemplateRow {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'in_app';
  variables: string[];
  usageCount: number;
  subject?: string | null;
  body: string;
}

const CHANNEL_COLORS: Record<TemplateRow['type'], string> = {
  email: 'blue',
  sms: 'green',
  in_app: 'orange',
};

function getRussianPluralCategory(count: number): 'one' | 'few' | 'many' {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'one';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
  return 'many';
}

function buildSnippet(template: TemplateRow): string {
  const parts = [template.subject ?? '', template.body].map((part) => part.trim()).filter(Boolean);
  return parts.join(' · ');
}

function TemplateEditor({
  template,
  isSaving,
  onSave,
}: {
  template: TemplateRow | null;
  isSaving: boolean;
  onSave: (template: TemplateRow, input: { body: string; subject?: string }) => Promise<void>;
}): ReactElement {
  const { t } = useTranslation();
  const [body, setBody] = useState(template?.body ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');

  async function handleSave(): Promise<void> {
    if (!template) return;
    await onSave(template, {
      body,
      ...(template.type === 'email' ? { subject } : {}),
    });
  }

  return (
    <Card
      size="small"
      className="lg:sticky lg:top-0"
      style={{ borderColor: '#e7e5e4', background: '#fffdfa' }}
      styles={{ body: { padding: 16 } }}
    >
      {template ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <Text strong style={{ color: '#44403c', fontSize: 15 }}>
                {t('crm.templates.editor_title')}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {template.name}
              </Text>
            </div>
            <Tag style={{ fontSize: 11 }} data-testid="template-editor-meta">
              {t('crm.templates.editor.usage_count', { count: template.usageCount })} ·{' '}
              {t(`crm.templates.editor.variables_count_${getRussianPluralCategory(template.variables.length)}` as never, {
                count: template.variables.length,
              })}
            </Tag>
          </div>

          {template.type === 'email' && (
            <Form.Item
              label={
                <label
                  htmlFor="template-subject"
                  style={{ fontSize: 11, color: '#78716c', textTransform: 'uppercase', fontWeight: 600 }}
                >
                  {t('crm.templates.subject')}
                </label>
              }
            >
              <Input
                id="template-subject"
                aria-label={t('crm.templates.subject')}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </Form.Item>
          )}

          <div>
            <Text
              style={{ display: 'block', marginBottom: 8, color: '#78716c', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}
            >
              {t('crm.templates.body')}
            </Text>
            <TextArea
              id="template-body"
              data-testid="template-editor-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              style={{ minHeight: 220, fontFamily: 'monospace', fontSize: 13 }}
              autoSize={{ minRows: 8, maxRows: 18 }}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {template.variables.join(', ') || t('crm.templates.no_variables')}
            </Text>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={isSaving}
              disabled={isSaving}
              onClick={() => void handleSave()}
              data-testid="template-save"
            >
              {t('crm.templates.save')}
            </Button>
          </div>
        </div>
      ) : (
        <Empty
          image={<FileTextOutlined style={{ color: '#d6d3d1', fontSize: 42 }} />}
          description={<Text type="secondary">{t('crm.templates.editor_empty')}</Text>}
        />
      )}
    </Card>
  );
}

export function TemplatesPage(): ReactElement {
  const { t } = useTranslation();
  const [channel, setChannel] = useState<TemplateChannel>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const templatesQuery = useTemplates({});
  const updateMutation = useUpdateTemplateMutation();
  const createMutation = useCreateTemplateMutation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm] = Form.useForm<{ name: string; type: 'email' | 'sms' | 'in_app'; subject?: string; body: string }>();

  const templates = templatesQuery.data ?? [];

  const visibleTemplates = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return templates.filter((template) => {
      if (channel !== 'all' && template.type !== channel) return false;
      if (!normalizedQuery) return true;

      const haystack = [template.name, template.subject ?? '', template.body].join(' ').toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [channel, searchQuery, templates]);

  const selectedTemplate = useMemo(
    () => visibleTemplates.find((template) => template.id === selectedId) ?? null,
    [selectedId, visibleTemplates],
  );

  const summary = useMemo(() => {
    return {
      total: templates.length,
      email: templates.filter((template) => template.type === 'email').length,
      sms: templates.filter((template) => template.type === 'sms').length,
      inApp: templates.filter((template) => template.type === 'in_app').length,
    };
  }, [templates]);

  const isFiltered = channel !== 'all' || searchQuery.trim().length > 0;
  const pagination = useDataViewPagination(visibleTemplates);
  const activeTemplate = selectedTemplate ?? pagination.paginatedItems[0] ?? null;

  async function handleSave(template: TemplateRow, input: { body: string; subject?: string }): Promise<void> {
    await updateMutation.mutateAsync({
      templateId: template.id,
      input,
    });
  }

  async function handleCreate(values: { name: string; type: 'email' | 'sms' | 'in_app'; subject?: string; body: string }): Promise<void> {
    const created = await createMutation.mutateAsync({
      name: values.name.trim(),
      type: values.type,
      body: values.body.trim(),
      ...(values.type === 'email' && values.subject?.trim() ? { subject: values.subject.trim() } : {}),
    });
    setIsCreateOpen(false);
    createForm.resetFields();
    setSelectedId(created.id);
  }

  function handleResetFilters(): void {
    setChannel('all');
    setSearchQuery('');
    pagination.resetPage();
  }

  function openTemplate(template: TemplateRow): void {
    setSelectedId(template.id);
  }

  const columns: ColumnsType<TemplateRow> = [
    {
      title: t('crm.templates.table.template'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <div className="flex min-w-0 flex-col gap-1">
          <Text strong style={{ color: '#44403c', fontSize: 13 }}>
            {name}
          </Text>
          <Text
            type="secondary"
            ellipsis={{ tooltip: buildSnippet(record) }}
            style={{ maxWidth: 520, fontSize: 12 }}
          >
            {buildSnippet(record) || t('crm.templates.no_preview')}
          </Text>
        </div>
      ),
    },
    {
      title: t('crm.templates.channel_label'),
      dataIndex: 'type',
      key: 'type',
      width: 132,
      render: (type: TemplateRow['type']) => (
        <Tag color={CHANNEL_COLORS[type]} style={{ margin: 0, fontSize: 11, textTransform: 'uppercase' }}>
          {t(`crm.templates.channel.${type}` as never)}
        </Tag>
      ),
    },
    {
      title: t('crm.templates.variables'),
      dataIndex: 'variables',
      key: 'variables',
      width: 220,
      render: (vars: string[]) => (
        <div className="flex flex-wrap gap-1">
          {vars.slice(0, 3).map((variable) => (
            <Tag key={variable} style={{ margin: 0, fontFamily: 'monospace', fontSize: 11 }}>
              {variable}
            </Tag>
          ))}
          {vars.length > 3 ? <Tag style={{ margin: 0, fontSize: 11 }}>+{vars.length - 3}</Tag> : null}
          {vars.length === 0 ? <Text disabled style={{ fontSize: 12 }}>—</Text> : null}
        </div>
      ),
    },
    {
      title: t('crm.templates.usage_count'),
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 130,
      align: 'right',
      render: (count: number) => (
        <Text style={{ color: '#78716c', fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{count}</Text>
      ),
    },
    {
      title: t('crm.templates.table.action'),
      key: 'action',
      width: 120,
      align: 'center',
      render: (_: unknown, record) => (
        <Button
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            openTemplate(record);
          }}
        >
          {t('crm.templates.open')}
        </Button>
      ),
    },
  ];

  return (
    <div className="animate-fade-in flex h-full flex-col gap-6">
      <header className="z-10 flex w-full shrink-0 items-center justify-between gap-4">
        <div>
          <h1 className="m-0 mb-1 font-bold leading-tight tracking-tight" style={{ color: '#2d2418', fontSize: 28 }}>
            {t('crm.templates.title')}
          </h1>
          <Text type="secondary" style={{ display: 'block', fontSize: 13, marginTop: 4 }}>
            <span data-testid="templates-visible-count">{visibleTemplates.length} из {summary.total}</span>{' '}
            {t('crm.templates.subtitle_unit')} · Email {summary.email} · SMS {summary.sms} · {t('crm.templates.channel.in_app')} {summary.inApp}
          </Text>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateOpen(true)}
            data-testid="template-create-open"
            style={{ borderRadius: 8, height: 36, padding: '0 16px', fontWeight: 500 }}
          >
            {t('crm.templates.new')}
          </Button>
          <Tooltip title={t('crm.templates.refresh')}>
            <Button
              aria-label={t('crm.templates.refresh')}
              icon={<ReloadOutlined />}
              onClick={() => void templatesQuery.refetch()}
              style={{ borderRadius: 8, height: 36, width: 36 }}
            />
          </Tooltip>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div
          className="flex w-full shrink-0 items-center gap-4 rounded-2xl border bg-white px-4 py-3 shadow-sm"
          style={{ borderColor: '#e7e5e4' }}
        >
          <div className="flex-1" style={{ maxWidth: 440 }}>
            <Input
              id="templates-search"
              aria-label={t('crm.templates.search_label')}
              placeholder={t('crm.templates.search_placeholder')}
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                pagination.resetPage();
              }}
              variant="borderless"
              className="transition-colors"
              style={{ backgroundColor: '#f5f5f4', borderRadius: 8, padding: '8px 12px' }}
              allowClear
            />
          </div>

          <div className="ml-auto flex items-center gap-3">
            <select
              value={channel}
              onChange={(event) => {
                setChannel(event.target.value as TemplateChannel);
                pagination.resetPage();
              }}
              aria-label={t('crm.templates.channel_filter_label')}
              className="bg-transparent font-medium transition-colors"
              style={{
                height: 36,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: '0 12px',
                color: '#44403c',
                fontSize: 14,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all">{t('crm.templates.filter.all')}</option>
              <option value="email">{t('crm.templates.channel.email')}</option>
              <option value="sms">{t('crm.templates.channel.sms')}</option>
              <option value="in_app">{t('crm.templates.channel.in_app')}</option>
            </select>

            {isFiltered ? (
              <Button onClick={handleResetFilters} style={{ borderRadius: 8, height: 36 }}>
                {t('crm.templates.reset_filters')}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:items-start">
          <div
            className="min-h-0 flex-1 lg:self-stretch"
            style={{ background: '#fffdfa', border: '1px solid #e7e5e4', borderRadius: 8, overflow: 'hidden' }}
          >
            {templatesQuery.isError ? (
              <div className="p-6">
                <Alert
                  type="error"
                  showIcon
                  title={t('crm.templates.load_error')}
                  description={t('crm.templates.load_error_description')}
                  action={
                    <Button size="small" onClick={() => void templatesQuery.refetch()}>
                      {t('crm.templates.retry')}
                    </Button>
                  }
                />
              </div>
            ) : templatesQuery.isLoading ? (
              <div aria-busy="true" aria-label={t('crm.templates.loading')} className="flex flex-col gap-3 p-6">
                {[1, 2, 3, 4].map((item) => (
                  <Skeleton key={item} active paragraph={{ rows: 1 }} />
                ))}
              </div>
            ) : (
              <Table<TemplateRow>
                dataSource={visibleTemplates}
                columns={columns}
                rowKey="id"
                size="small"
                pagination={pagination.pagination}
                data-testid="template-list"
                onRow={(record) => ({
                  'data-testid': `template-row-${record.id}`,
                  style: {
                    cursor: 'pointer',
                    background: activeTemplate?.id === record.id ? '#fffbeb' : undefined,
                  },
                  onClick: () => openTemplate(record),
                  onMouseEnter: (event) => {
                    event.currentTarget.style.backgroundColor = '#fffbeb';
                  },
                  onMouseLeave: (event) => {
                    event.currentTarget.style.backgroundColor = activeTemplate?.id === record.id ? '#fffbeb' : 'transparent';
                  },
                })}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={<Text type="secondary">{isFiltered ? t('crm.templates.empty_filtered') : t('crm.templates.empty')}</Text>}
                    />
                  ),
                }}
                style={{ fontSize: 13 }}
              />
            )}
          </div>

          <aside className="w-full shrink-0 lg:w-[380px] xl:w-[420px]">
            <TemplateEditor
              key={activeTemplate?.id ?? 'empty'}
              template={activeTemplate}
              isSaving={updateMutation.isPending}
              onSave={handleSave}
            />
          </aside>
        </div>
      </div>

      <Modal
        title={t('crm.templates.new')}
        open={isCreateOpen}
        onCancel={() => setIsCreateOpen(false)}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(values) => void handleCreate(values)}
          requiredMark={false}
          initialValues={{ type: 'email' }}
        >
          <Form.Item name="name" label={t('crm.templates.name')} rules={[{ required: true }]}>
            <Input
              id="template-new-name"
              aria-label={t('crm.templates.name')}
              placeholder={t('crm.templates.name_placeholder')}
            />
          </Form.Item>

          {/* Native select is required for Playwright selectOption() */}
          <Form.Item
            name="type"
            label={t('crm.templates.channel_label')}
            getValueFromEvent={(e: React.ChangeEvent<HTMLSelectElement>) => e.target.value}
          >
            <select
              id="template-new-type"
              aria-label={t('crm.templates.channel_label')}
              style={{
                width: '100%',
                height: 32,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: '0 8px',
                fontSize: 13,
                background: '#fefbf6',
                color: '#44403c',
                outline: 'none',
              }}
            >
              <option value="email">{t('crm.templates.channel.email')}</option>
              <option value="sms">{t('crm.templates.channel.sms')}</option>
              <option value="in_app">{t('crm.templates.channel.in_app')}</option>
            </select>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
            {({ getFieldValue }) =>
              getFieldValue('type') === 'email' ? (
                <Form.Item name="subject" label={t('crm.templates.subject')}>
                  <Input id="template-new-subject" aria-label={t('crm.templates.subject')} />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item name="body" label={t('crm.templates.new_body')} rules={[{ required: true }]}>
            <TextArea
              id="template-new-body"
              aria-label={t('crm.templates.new_body')}
              autoSize={{ minRows: 5, maxRows: 12 }}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>
          <Divider />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsCreateOpen(false)}>{t('crm.common.cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={createMutation.isPending} icon={<PlusOutlined />} data-testid="template-create-submit">
              {t('crm.templates.create_submit')}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
